"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, CircleCheck as CheckCircle2, Clock, Hammer, HardHat, House as Home, PhoneCall, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const sitePhotosQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 50
});
function ChimneyHeroImageConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="size-full bg-muted animate-pulse" />;
  }
  const matchedPhoto = posts.find(post => post.customFields?.photo_name === "Chimney Scaffolding Hero Image" || post.title === "Chimney Scaffolding Hero Image");
  const imgSrc = matchedPhoto?.customFields?.image || matchedPhoto?.featuredImage;
  const altText = matchedPhoto?.customFields?.alt_text || "Engineered chimney access scaffold tower on a residential roof in the South East";
  if (imgSrc) {
    const image = {
      src: imgSrc,
      srcset: "",
      sizes: ""
    };
    return <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={altText} loading="lazy" decoding="async" className="size-full object-cover object-center" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/chimney_scaffold_tower_structure.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/chimney_scaffold_tower_structure.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/chimney_scaffold_tower_structure.png-wvc-sizes" alt="Engineered chimney access scaffold tower on a residential roof in the South East" loading="lazy" decoding="async" className="size-full object-cover object-center" />;
}
const audiences = [{
  role: "Homeowners",
  desc: "Neat, respectful domestic installations with full ground and garden protection.",
  icon: Home
}, {
  role: "Roofing Contractors",
  desc: "Rigid, level working platforms positioned precisely where your roofers need them.",
  icon: Hammer
}, {
  role: "Builders & Masons",
  desc: "Rated saddle stages and towers designed for heavy brickwork, repointing, and removals.",
  icon: Building2
}];
const useCases = [{
  title: "Stack repair & repointing",
  description: "Stable, 360-degree perimeter boards allowing masonry teams to safely repoint, re-bed ridge tiles, and replace cracked pots.",
  icon: Wrench
}, {
  title: "Chimney removal & reduction",
  description: "Heavy-duty load-bearing towers configured for controlled brick-by-brick teardowns down to roofline or ceiling level.",
  icon: Hammer
}, {
  title: "Roof-level access towers",
  description: "Freestanding or tied tube-and-fitting towers giving safe, continuous access to hard-to-reach central stacks without tile damage.",
  icon: HardHat
}];
const features = ["Custom saddle stages engineered around dormers, conservatories, and valley gutters", "Heavy-duty protective boards and sheeting to shield driveways, lawns, and plants", "Prompt strike and clear-down as soon as masonry or roofing is signed off", "Compliant with TG20:21 design standards with full handrail and toeboard coverage"];
export default function ChimneyScaffolding() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="chimneyscaffoldinghero" data-nav="light" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24">
      {/* Decorative ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(5,32,250,0.06),transparent)]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Badging & Main H1 Header */}
        <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={fadeIn.transition} className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2">
            <Badge variant="outline" className="border-border bg-card px-3 py-1 text-xs font-semibold text-foreground shadow-xs">
              <ShieldCheck className="size-3.5 text-primary" />
              NASC & CHAS Accreditations
            </Badge>
          </div>

          <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Chimney Scaffolding
          </h1>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Safe, purpose-built chimney access scaffolding for repair, repointing, lead flashing, and stack removal on residential, modern, and period properties across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/ContactRequestQuotePage">
                <PhoneCall className="size-4" />
                Request a chimney quote
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="shadow-xs">
              <Link to="/ResidentialScaffoldingPage">
                View residential services
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Audience Grid */}
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-6">
          {audiences.map((item, i) => {
          const IconComponent = item.icon;
          return <div key={item.role} data-index={i} className="group rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-primary">
                    <IconComponent className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tailored for</span>
                    <h2 className="font-default text-base font-bold text-foreground">{item.role}</h2>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-normal text-muted-foreground">
                  {item.desc}
                </p>
              </div>;
        })}
        </div>

        {/* Core Capabilities & Visual Grid */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Use Cases & BMF Standard */}
          <div className="lg:col-span-7">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-semibold tracking-wide text-primary uppercase">Core Applications</span>
                <h2 className="mt-1 font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  Built for precision at roof level
                </h2>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                  Whether tackling an unstable Victorian chimney stack, renewing lead trays, or stripping redundant chimneys, our modular tube-and-fitting access provides a firm, protected workstation without placing loads on fragile roof tiles.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {useCases.map((uc, idx) => {
                const UcIcon = uc.icon;
                return <Card key={uc.title} data-index={idx} className="border-border bg-card shadow-xs">
                      <CardHeader className="p-4 pb-2">
                        <div className="mb-2 flex size-8 items-center justify-center rounded-md bg-muted text-primary">
                          <UcIcon className="size-4" />
                        </div>
                        <CardTitle className="text-sm font-semibold leading-snug text-foreground">
                          {uc.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {uc.description}
                        </p>
                      </CardContent>
                    </Card>;
              })}
              </div>

              {/* What BMF Provides Checklist */}
              <div className="rounded-xl border border-border bg-muted/40 p-5">
                <h3 className="font-default text-sm font-bold uppercase tracking-wider text-foreground">
                  What BMF Scaffolding guarantees on every chimney project
                </h3>
                <ul className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {features.map((feature, fIdx) => <li key={feature} data-index={fIdx} className="flex items-start gap-2 text-xs leading-relaxed text-foreground">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{feature}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* Right Column: Hero Graphic Card & Safety Experience Box */}
          <div className="lg:col-span-5">
            <div className="space-y-4">
              {/* Image Preview Container */}
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-md">
                <div className="relative aspect-4/3 w-full bg-muted">
                  <PostsProvider wp_query={{
                  post_type: "site_photo",
                  per_page: 50,
                  status: ["publish"]
                }}>
                    <ChimneyHeroImageConsumer />
                  </PostsProvider>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/80 px-2.5 py-1 text-xs font-medium backdrop-blur-xs">
                      <Sparkles className="size-3.5 text-primary" />
                      Saddle stages & access towers
                    </span>
                    <span className="text-xs font-medium text-white/90">South East & Home Counties</span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-default text-base font-bold text-foreground">
                    Safety-led installation by Pete Wood & team
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    With over 20 years of operational scaffolding experience and carrying forward Adam Story&apos;s legacy, BMF Scaffolding Ltd balances technical competence with direct, family-run care. Every chimney tower is planned carefully around conservatory roofs, solar arrays, and landscaping.
                  </p>

                  <Separator className="my-4" />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="size-4 text-primary" />
                      <span>Fast turnaround & prompt strike</span>
                    </div>
                    <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs font-semibold">
                      <Link to="/ContactRequestQuotePage">
                        Get project pricing
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}